import type { TextFontSizeModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { parsePixelValue } from "@agent-ui/features/modifiers/utils/parseComputedValue";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { FONT_SIZE_OPTIONS } from "@v2/features/modifiers/constants";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

const ALLOWED_NON_UNIT_VALUES = ["auto"];

interface FontSizeModifierProps {
  onChange: (modifiers: TextFontSizeModifierValue[]) => void;
}

const FontSizeModifier: React.FC<FontSizeModifierProps> = ({ onChange }) => {
  const currentValue =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return parsePixelValue(computedStyles.fontSize) ?? null;
      },
    }) ?? null;

  const displayValue = currentValue != null ? `${currentValue}px` : "Auto";

  const menuOptions = FONT_SIZE_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  const handleChange = (newValue: string | null) => {
    if (newValue === null || newValue.toLowerCase() === "auto") {
      onChange([{ type: "fontSize" as const, value: null }]);
      return;
    }

    const result = parseUnit(
      newValue,
      { value: 0, unit: "px" },
      "fontSize",
      "px",
    );

    onChange([
      {
        type: "fontSize" as const,
        value: Number(result.value),
      },
    ]);
  };

  return (
    <div className="font-size-modifier">
      <LengthInputSelector
        field="fontSize"
        value={displayValue}
        onChange={handleChange}
        placeholder="1px"
        metrics={["px"]}
        unitDefaults={{ value: "1", unit: "px" }}
        menuOptions={menuOptions}
        minValues={{ px: 1 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
        allowedNonUnitValues={ALLOWED_NON_UNIT_VALUES}
        transformValue={(value: string) => {
          return value.toLowerCase() === "auto" ? "Auto" : value;
        }}
      />
    </div>
  );
};

export default FontSizeModifier;
